"use client";

import type { ReactNode } from "react";
import { sailingFreshnessAt, type SailingFreshnessInput } from "@/lib/conditions/sailing-freshness";
import { useHydrated, useMinuteClock } from "@/lib/hooks/use-minute-clock";

// The sailing hero's freshness label and chips, judged in the browser on its own clock (the page
// is statically rendered and rebuilt every 3 hours; lib/conditions/sailing-freshness.ts). The
// static HTML makes no "today" claim: the rank link appears only after hydration, once the
// browser's clock says the row is today's current score.

type Props = Readonly<{ freshness: SailingFreshnessInput; serverNowMs: number }>;

/** "Sailing forecast", "Forecast issued 21:40 BST" (the nightly gap) or "Forecast awaiting update". */
export function SailingFreshnessLabel({ freshness, serverNowMs }: Props) {
  const { stale, gapForecast } = sailingFreshnessAt(freshness, useMinuteClock(serverNowMs));
  return <>{stale ? gapForecast?.issuedLabel ?? "Forecast awaiting update" : "Sailing forecast"}</>;
}

/** The stale or gap chip, or `current` (the rank link) once hydrated while the row is current. */
export function SailingFreshnessChip({ freshness, serverNowMs, current }: Props & { current?: ReactNode }) {
  const hydrated = useHydrated();
  const { stale, gapForecast } = sailingFreshnessAt(freshness, useMinuteClock(serverNowMs));
  if (!stale) return <>{hydrated ? current ?? null : null}</>;
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/20 border border-amber-400/40 text-[11px] font-semibold text-amber-200">
      <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
      {gapForecast ? "Previous forecast for today" : "Forecast awaiting update"}
    </span>
  );
}
